import { useState } from "react";
import { DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ActionParameterInput } from "src/components/Actions/Common/ActionParameterInput";
import { FoodsSelectField } from "src/components/EntityForm/FoodsFields";
import type { TemplateActionField } from "src/api/client-api/actionPlanTemplates/useTemplateActionOptions";
import type { TemplateAction, TemplateFieldValue } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import {
    initialTemplateFieldValues,
    templateAllowsBounds,
    templateBoundsError,
    templateBoundsUnit,
    templateFieldUnit,
    templateFieldType,
    templateShareBases,
    templateShareUnit,
    templateShareUnitMismatch,
    templateShareValueError,
    validTemplateFields,
} from "./templateActionForm";

type ParametersProps = {
    editing?: TemplateAction;
    onSave: (action: TemplateAction) => void;
    onClose: () => void;
    actionId: number;
    title: string;
    fields: TemplateActionField[];
};

export const TemplateActionParameters = (props: ParametersProps) => {
    const { actionId, title, fields, editing, onSave, onClose } = props;

    const { t, i18n } = useTranslation();
    const theme = useTheme();

    const [values, setValues] = useState(() => initialTemplateFieldValues(fields, editing?.fieldValues ?? []));

    const update = (entry: TemplateFieldValue, changes: Partial<TemplateFieldValue>) =>
        setValues((current) =>
            current.map((value) =>
                value.actionFieldId === entry.actionFieldId && value.position === entry.position ?
                    { ...value, ...changes }
                :   value,
            ),
        );

    const valid = validTemplateFields(fields, values);
    const visibleFields = fields.filter((field) => !field.hidden);
    const valueModes = [
        { id: "fixed" as const, name: t("settings.action_plan_templates.editor.fixed_value", "Fixed value") },
        {
            id: "share" as const,
            name: t("settings.action_plan_templates.editor.share_value", "Percentage of the user's value"),
        },
    ];
    const selectWidth = theme.spacing(37.5);

    return (
        <>
            <DialogContent sx={{ px: theme.spacing(4), py: theme.spacing(3.5), bgcolor: "grey.200" }}>
                <Stack gap={theme.spacing(2.75)}>
                    <Typography variant="h4" sx={{ "&&": { fontSize: theme.spacing(1.875), lineHeight: 1.6 } }}>
                        {title}
                    </Typography>
                    {visibleFields.map((field, index) => {
                        const entry = values.find((value) => value.actionFieldId === field.id);
                        if (!entry) return null;

                        const bases = templateShareBases(field);
                        const shareMode = entry.mode === "share";
                        const unit = shareMode ? "%" : templateFieldUnit(field);
                        const isLast = index === visibleFields.length - 1;

                        return (
                            <Stack
                                key={`${field.id}-template-parameter-field`}
                                component="fieldset"
                                gap={theme.spacing(1.25)}
                                sx={{
                                    border: 0,
                                    m: 0,
                                    p: 0,
                                    minWidth: 0,
                                    pb: isLast ? 0 : theme.spacing(2.75),
                                    borderBottom: isLast ? 0 : 1,
                                    borderColor: "divider",
                                }}
                            >
                                <Typography
                                    component="legend"
                                    variant="body-regular"
                                    sx={{
                                        mb: theme.spacing(1.25),
                                        "&&": { fontSize: theme.spacing(1.75), lineHeight: 1.6, fontWeight: 500 },
                                    }}
                                >
                                    {field.isMultivalue ?
                                        t(
                                            "settings.action_plan_templates.editor.value_for_each_answer",
                                            "Value for each answered item",
                                        )
                                    :   field.question}
                                </Typography>
                                {field.isMultivalue && (
                                    <Typography component="p" variant="body-regular">
                                        {t(
                                            "settings.action_plan_templates.editor.multivalue_explanation",
                                            "Set one value for {{field}}. It applies separately to each answered item; unanswered and not applicable items are skipped.",
                                            { field: field.question },
                                        )}
                                    </Typography>
                                )}
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    alignItems={{ sm: "center" }}
                                    gap={theme.spacing(1.5)}
                                    sx={{ minWidth: 0 }}
                                >
                                    {(bases.length > 0 || shareMode) && (
                                        <FoodsSelectField<(typeof valueModes)[number]>
                                            appearance="product-v2"
                                            accessibleLabel={t(
                                                "settings.action_plan_templates.editor.value_mode",
                                                "Value type",
                                            )}
                                            disableSearch
                                            options={valueModes}
                                            selectedOption={
                                                valueModes.find((mode) => mode.id === (entry.mode ?? "fixed")) ?? null
                                            }
                                            getId={(mode) => mode.id}
                                            getName={(mode) => mode.name}
                                            placeholder={t(
                                                "settings.action_plan_templates.editor.value_mode",
                                                "Value type",
                                            )}
                                            searchPlaceholder=""
                                            emptyOptionsText={t(
                                                "settings.action_plan_templates.form.no_options",
                                                "No options available",
                                            )}
                                            noResultText={t(
                                                "settings.action_plan_templates.form.no_options",
                                                "No options available",
                                            )}
                                            stackProps={{ sx: { width: "100%", maxWidth: selectWidth } }}
                                            boxOptionsProps={{ width: selectWidth }}
                                            onChange={(selected) => {
                                                if (selected === null) return;
                                                const mode = selected.id;
                                                const base = bases.length === 1 ? bases[0] : undefined;
                                                setValues((current) => {
                                                    const entries = current.filter(
                                                        (value) => value.actionFieldId === field.id,
                                                    );
                                                    const first = entries[0];
                                                    if (!first) return current;
                                                    return [
                                                        ...current.filter((value) => value.actionFieldId !== field.id),
                                                        {
                                                            ...first,
                                                            value: "",
                                                            position: 0,
                                                            mode,
                                                            baseSsqPropertyId: mode === "share" ? base?.id : null,
                                                            boundsEnabled: false,
                                                            minimumValue: null,
                                                            maximumValue: null,
                                                        },
                                                    ];
                                                });
                                            }}
                                        />
                                    )}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={theme.spacing(1.5)}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        <ActionParameterInput
                                            maxWidth={theme.spacing(37.5)}
                                            value={entry.value}
                                            codes={entry.dataListOptionCodes}
                                            options={field.dataList?.options}
                                            type={templateFieldType(field)}
                                            unit={unit}
                                            locale={i18n.resolvedLanguage ?? "en"}
                                            label={
                                                field.isMultivalue ?
                                                    shareMode ?
                                                        t(
                                                            "settings.action_plan_templates.editor.share_for_each_answer",
                                                            "Percentage of each answered value",
                                                        )
                                                    :   t(
                                                            "settings.action_plan_templates.editor.value_for_each_answer",
                                                            "Value for each answered item",
                                                        )

                                                :   field.question
                                            }
                                            onChange={(value) => update(entry, { value, dataListOptionCodes: [] })}
                                            onOptionsChange={(selection) => {
                                                const options =
                                                    selection === null ? []
                                                    : Array.isArray(selection) ? selection
                                                    : [selection];
                                                update(entry, {
                                                    value: options.map((option) => option.value).join("|"),
                                                    dataListOptionCodes: options.map((option) => option.code),
                                                });
                                            }}
                                        />
                                        {unit && (
                                            <Typography
                                                component="p"
                                                variant="body-regular"
                                                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                            >
                                                {unit}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                                {bases.length > 1 && shareMode && (
                                    <FoodsSelectField<(typeof bases)[number]>
                                        appearance="product-v2"
                                        label={t(
                                            "settings.action_plan_templates.editor.base_property",
                                            "Reference property",
                                        )}
                                        required
                                        disableSearch
                                        options={bases}
                                        selectedOption={
                                            bases.find((base) => base.id === entry.baseSsqPropertyId) ?? null
                                        }
                                        getId={(base) => base.id}
                                        getName={(base) => base.title}
                                        placeholder={t("select.generic.placeholder", "Select an option")}
                                        searchPlaceholder=""
                                        emptyOptionsText={t(
                                            "settings.action_plan_templates.form.no_options",
                                            "No options available",
                                        )}
                                        noResultText={t(
                                            "settings.action_plan_templates.form.no_options",
                                            "No options available",
                                        )}
                                        stackProps={{ sx: { maxWidth: selectWidth } }}
                                        boxOptionsProps={{ width: selectWidth }}
                                        onChange={(base) => {
                                            if (base === null || entry.baseSsqPropertyId === base.id) return;
                                            update(entry, {
                                                baseSsqPropertyId: base.id,
                                                boundsEnabled: false,
                                                minimumValue: null,
                                                maximumValue: null,
                                            });
                                        }}
                                    />
                                )}
                                {bases.length === 1 && shareMode && entry.baseSsqPropertyId === bases[0].id && (
                                    <Typography component="p" variant="body-regular">
                                        {t("settings.action_plan_templates.editor.base_property", "Reference property")}
                                        : {bases[0].title}
                                    </Typography>
                                )}
                                {entry.value !== "" && templateShareValueError(entry) && (
                                    <Typography component="p" variant="body-regular" role="alert" color="error">
                                        {t(
                                            "settings.action_plan_templates.editor.invalid_share_range",
                                            "Enter a percentage between 0 (excluded) and 100.",
                                        )}
                                    </Typography>
                                )}
                                {templateShareUnitMismatch(field, entry) && (
                                    <Typography component="p" variant="body-regular" role="alert" color="error">
                                        {t(
                                            "settings.action_plan_templates.editor.share_unit_mismatch",
                                            "The unit of the selected property must match the unit of this field.",
                                        )}
                                    </Typography>
                                )}
                                {(templateAllowsBounds(field, entry) || entry.boundsEnabled) && (
                                    <Button
                                        variant="text"
                                        sx={{
                                            alignSelf: "flex-start",
                                            p: 0,
                                            "&.Mui-focusVisible": { outline: "auto" },
                                        }}
                                        aria-expanded={entry.boundsEnabled ?? false}
                                        startIcon={<FoodsIcon icon={entry.boundsEnabled ? "minus" : "plus"} size={1} />}
                                        onClick={() =>
                                            update(entry, {
                                                boundsEnabled: !entry.boundsEnabled,
                                                minimumValue: entry.boundsEnabled ? null : entry.minimumValue,
                                                maximumValue: entry.boundsEnabled ? null : entry.maximumValue,
                                            })
                                        }
                                    >
                                        <Typography component="span" variant="small-bold" color="inherit">
                                            {entry.boundsEnabled ?
                                                t(
                                                    "settings.action_plan_templates.editor.remove_limits",
                                                    "Remove limits",
                                                )
                                            :   t("settings.action_plan_templates.editor.add_limits", "Add limits")}
                                        </Typography>
                                    </Button>
                                )}
                                {entry.boundsEnabled && !templateAllowsBounds(field, entry) && (
                                    <Typography component="p" variant="body-regular" role="alert" color="error">
                                        {t(
                                            "settings.action_plan_templates.editor.bounds_unsupported",
                                            "Limits are not available for a field that feeds several results. Remove them to save.",
                                        )}
                                    </Typography>
                                )}
                                {entry.boundsEnabled && (
                                    <Stack direction={{ xs: "column", sm: "row" }} gap={theme.spacing(2)}>
                                        <Stack gap={theme.spacing(1)}>
                                            <Typography component="p" variant="body-regular">
                                                {t(
                                                    "settings.action_plan_templates.editor.minimum_value",
                                                    "Minimum value",
                                                )}
                                            </Typography>
                                            <ActionParameterInput
                                                value={entry.minimumValue ?? ""}
                                                type="number"
                                                unit={
                                                    shareMode ?
                                                        templateShareUnit(field, entry.baseSsqPropertyId)
                                                    :   templateBoundsUnit(field)
                                                }
                                                locale={i18n.resolvedLanguage ?? "en"}
                                                label={t(
                                                    "settings.action_plan_templates.editor.minimum_value",
                                                    "Minimum value",
                                                )}
                                                onChange={(minimumValue) => update(entry, { minimumValue })}
                                                onOptionsChange={() => undefined}
                                            />
                                        </Stack>
                                        <Stack gap={theme.spacing(1)}>
                                            <Typography component="p" variant="body-regular">
                                                {t(
                                                    "settings.action_plan_templates.editor.maximum_value",
                                                    "Maximum value",
                                                )}
                                            </Typography>
                                            <ActionParameterInput
                                                value={entry.maximumValue ?? ""}
                                                type="number"
                                                unit={
                                                    shareMode ?
                                                        templateShareUnit(field, entry.baseSsqPropertyId)
                                                    :   templateBoundsUnit(field)
                                                }
                                                locale={i18n.resolvedLanguage ?? "en"}
                                                label={t(
                                                    "settings.action_plan_templates.editor.maximum_value",
                                                    "Maximum value",
                                                )}
                                                onChange={(maximumValue) => update(entry, { maximumValue })}
                                                onOptionsChange={() => undefined}
                                            />
                                        </Stack>
                                    </Stack>
                                )}
                                {templateBoundsError(entry) && (
                                    <Typography component="p" variant="body-regular" role="alert" color="error">
                                        {templateBoundsError(entry) === "range" ?
                                            t(
                                                "settings.action_plan_templates.editor.invalid_bounds_range",
                                                "The minimum cannot be greater than the maximum.",
                                            )
                                        :   t(
                                                "settings.action_plan_templates.editor.invalid_bounds_number",
                                                "Enter a valid number for each limit.",
                                            )
                                        }
                                    </Typography>
                                )}
                            </Stack>
                        );
                    })}
                    {visibleFields.length === 0 && (
                        <Typography component="p" variant="body-regular">
                            {t(
                                "settings.action_plan_templates.editor.no_parameters",
                                "This action has no parameters to fill in.",
                            )}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    px: theme.spacing(3),
                    py: theme.spacing(2.5),
                    justifyContent: "space-between",
                    borderTop: 1,
                    borderColor: "divider",
                }}
            >
                <Button variant="text" onClick={onClose} endIcon={<FoodsIcon icon="close" size={1} />}>
                    {t("settings.action_plan_templates.editor.cancel", "Cancel")}
                </Button>
                <Button
                    variant="primary"
                    startIcon={<FoodsIcon icon="check" size={1} />}
                    disabled={!valid}
                    onClick={() => onSave({ actionId, fieldValues: values, position: editing?.position ?? 0 })}
                >
                    {t("settings.action_plan_templates.editor.save", "Save")}
                </Button>
            </DialogActions>
        </>
    );
};
